import { PlayArrow } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Drawer, Grid, IconButton, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { EAttributeType } from '../../../../@types/AttributeTypes';
import { AttachmentAsset } from '../../../../models/standard/AttachmentAsset';
import AttachmentModel, { AttachmentType } from '../../../../models/standard/AttachmentModel';
import AttributeModel from '../../../../models/standard/AttributeModel';
import { useAnnotations } from '../../../../provider/AnnotationsProvider';
import { usePlan } from '../../../../provider/PlanProvider';
import theme from '../../../../theme';
import { immersightBlue, immersightBlue75 } from '../../../../utils/colors';
import { AttachmentAttributeContent } from './AttachmentAttributeContent';
import { AttachmentImageContent } from './AttachmentImageContent';
import { AttachmentPanoramaContent } from './AttachmentPanoramaContent';
import { AttachmentPDFContent } from './AttachmentPDFContent';

interface AttachmentDrawerProps {
  id: string;
  open: boolean;
  hotspotId: number | null;
  onClose: () => void;
  resetSelection: () => void;
}

const AttachmentDrawer: React.FC<AttachmentDrawerProps> = ({
  id,
  open,
  hotspotId,
  onClose,
  resetSelection,
}) => {
  const { hotspots, deleteHotspot, updateHotspotAttachment } = useAnnotations();
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';
  const defaultsRegionKey = regionCode === 'US' ? 'US' : 'DE';
  const selectedHotspot = hotspots.find((h) => h.id === hotspotId);

  // --- DATA EXTRACTION ---
  const attributeData =
    selectedHotspot?.attachment.type === AttachmentType.ATTRIBUTE
      ? (selectedHotspot.attachment.data as AttributeModel)
      : undefined;

  const imageAttachment =
    selectedHotspot?.attachment.type === AttachmentType.IMAGE
      ? (selectedHotspot.attachment.data as AttachmentAsset)
      : undefined;

  const panoramaAttachment =
    selectedHotspot?.attachment.type === AttachmentType.PANORAMA
      ? (selectedHotspot.attachment.data as AttachmentAsset)
      : undefined;

  const pdfAttachment =
    selectedHotspot?.attachment.type === AttachmentType.PDF
      ? (selectedHotspot.attachment.data as AttachmentAsset)
      : undefined;

  // --- BUSINESS LOGIC ---
  useEffect(() => {
    if (
      attributeData?.type === EAttributeType.BOOL &&
      (attributeData.value === undefined || attributeData.value === null || attributeData.value === '')
    ) {
      const defaultObj = attributeData.defaults?.[defaultsRegionKey] || attributeData.defaults?.GLOBAL;
      const defaultValue = defaultObj?.value !== undefined && defaultObj.value !== null && String(defaultObj.value) !== '' ? String(defaultObj.value) : 'true';

      const attributeModel = new AttributeModel(
        attributeData.id,
        attributeData.key,
        defaultValue,
        attributeData.predecessorId,
        attributeData.type,
        attributeData.unit,
        attributeData.hidden,
        attributeData.forced,
        attributeData.ownerId,
        attributeData.userId,
        attributeData.publicFlag,
        attributeData.localization,
        attributeData.defaults,
        attributeData.validation,
        attributeData.unitFamily
      );
      const attachmentModel = new AttachmentModel(AttachmentType.ATTRIBUTE, attributeModel);
      updateHotspotAttachment(hotspotId!, attachmentModel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attributeData?.id, hotspotId]);

  const handleDelete = () => {
    if (hotspotId !== null) {
      deleteHotspot(hotspotId);
      resetSelection();
      onClose();
    }
  };

  const handleUpdateAttribute = (newValue: string) => {
    if (!attributeData || hotspotId === null || attributeData.forced) return;

    const updatedAttr = new AttributeModel(
      attributeData.id,
      attributeData.key,
      newValue,
      attributeData.predecessorId,
      attributeData.type,
      attributeData.unit,
      attributeData.hidden,
      attributeData.forced,
      attributeData.ownerId,
      attributeData.userId,
      attributeData.publicFlag,
      attributeData.localization,
      attributeData.defaults,
      attributeData.validation,
      attributeData.unitFamily
    );

    const attachmentModel = new AttachmentModel(AttachmentType.ATTRIBUTE, updatedAttr);
    updateHotspotAttachment(hotspotId, attachmentModel);
  };

  // --- NEW: Handle Unit Changes ---
  const handleUnitChange = (newUnit: string) => {
    if (!attributeData || hotspotId === null || attributeData.forced) return;

    const updatedAttr = new AttributeModel(
      attributeData.id,
      attributeData.key,
      attributeData.value, // Keep existing value
      attributeData.predecessorId,
      attributeData.type,
      newUnit, // Update the Unit
      attributeData.hidden,
      attributeData.forced,
      attributeData.ownerId,
      attributeData.userId,
      attributeData.publicFlag,
      attributeData.localization,
      attributeData.defaults,
      attributeData.validation,
      attributeData.unitFamily
    );

    const attachmentModel = new AttachmentModel(AttachmentType.ATTRIBUTE, updatedAttr);
    updateHotspotAttachment(hotspotId, attachmentModel);
  };

  const handleCloseDrawer = () => {
    resetSelection();
    onClose();
  };

  return (
    <Drawer
      key={id}
      anchor="right"
      open={open}
      variant={open ? 'permanent' : 'temporary'}
      sx={{
        '& .MuiPaper-root': {
          top: '64px',
          zIndex: 1000,
          width: '420px',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      {/* HEADER */}
      <Grid container direction="column" p={0}>
        <Grid
          item
          sx={{
            backgroundColor: immersightBlue,
            color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.5,
          }}
        >
          <Box sx={{ marginLeft: '16px' }}>
            <Typography variant="h6" sx={{ textTransform: 'uppercase' }}>
              <FormattedMessage id="AttachmentDrawer.title" defaultMessage="Attachment" />
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{ color: '#fff', '&:hover': { backgroundColor: 'transparent' } }}
          >
            <PlayArrow sx={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
      </Grid>

      {/* CONTENT */}
      <Grid container item direction="column" spacing={3} p={3}>
        
        {/* CASE 1: IMAGE */}
        {selectedHotspot && imageAttachment && (
          <Grid item>
            <AttachmentImageContent
              data={imageAttachment}
              onDelete={handleDelete}
            />
          </Grid>
        )}

        {/* CASE 2: PANORAMA */}
        {selectedHotspot && panoramaAttachment && (
          <Grid item>
            <AttachmentPanoramaContent
              data={panoramaAttachment}
              onDelete={handleDelete}
            />
          </Grid>
        )}

        {/* CASE 3: PDF */}
        {selectedHotspot && pdfAttachment && (
          <Grid item>
            <AttachmentPDFContent
              data={pdfAttachment}
              onDelete={handleDelete}
            />
          </Grid>
        )}

        {/* CASE 4: ATTRIBUTES */}
        {selectedHotspot && attributeData && (
          <Grid item>
            <AttachmentAttributeContent
              data={attributeData}
              onUpdate={handleUpdateAttribute}
              onUnitChange={handleUnitChange} 
            />
            
            <Button
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              disabled={hotspotId === null}
              sx={{
                marginTop: theme.spacing(2),
                backgroundColor: immersightBlue,
                '&:hover': { backgroundColor: immersightBlue75 },
              }}
            >
              <FormattedMessage id="AttachmentDrawer.deleteAttachment" defaultMessage="Attachment löschen" />
            </Button>
          </Grid>
        )}

      </Grid>
    </Drawer>
  );
};

export default AttachmentDrawer;
